// AP Document (provider invoice & related documents) — mirrors Finance.API ApDocument / ApDocumentDto.
import { ResponseTranslation } from '../Translations';
import { S3Object } from '../ap-payment/ap-payment';

/** Re-exported so the AP document screen can type its attachment list without reaching into ap-payment. */
export type { S3Object } from '../ap-payment/ap-payment';

export enum ApDocumentType {
  Invoice = 1,
  // Payment / PaymentRequest are tracker discriminators, NOT registrable document kinds.
  Payment = 2,
  PaymentRequest = 3,
  Receipt = 4,
  PurchaseReceipt = 5,
  Policy = 6,
  DebitNote = 7,
  AdvanceRequest = 8,
  AdvancePayment = 9,
  CreditNote = 10,
  /** DEPRECATED as a document kind (#4801): the Constancia de Retención moved to its own document and
   * collection (see models/withholding-document). Kept only as an API-side numbering discriminator — never
   * offer it in a document-type selector, and no AP document carries it any more. */
  Retention = 11,
}

/** Debit/Credit Notes are issued against an existing invoice (#4455/#4456). */
export const NOTE_AP_DOCUMENT_TYPES: ApDocumentType[] = [ApDocumentType.DebitNote, ApDocumentType.CreditNote];

export enum ApDocumentStatus {
  Draft = 1,
  Posted = 2,
  PartiallyPaid = 3,
  Paid = 4,
  Cancelled = 5,
}

export enum ApTaxType {
  Exempt = 1,
}

/**
 * Where the AP document's payment term was resolved from (#4784). The source document's own term wins over
 * the provider master's default. Resolved API-side and read-only on the form — when it came from a source
 * document, correcting the term means correcting that document.
 */
export enum PaymentTermSource {
  ProviderDefault = 1,
  PurchaseOrder = 2,
  Receipt = 3,
  /** #4785 — explicitly overridden on the form. Only reachable when no source document dictated the term. */
  Manual = 4,
  /** #6324 — a Credit/Debit Note's term, copied from the original document it adjusts. */
  RelatedDocument = 5,
}

/** Why a Credit/Debit Note was issued (#6324) — Sierra's four reasons. Mirrors Finance.Models ApNoteReason. */
export enum ApNoteReason {
  Quantity = 1,
  PriceOrValue = 2,
  Tax = 3,
  /** No invoice: the user picks each line's account. */
  Financial = 4,
}

/** How a Precio o valor note line states its adjustment (#6324). */
export enum ApNotePriceMode {
  CorrectUnitPrice = 1,
  FixedAmount = 2,
}

/** The optional "Causa" of a note (#6324). Which ones a note may carry comes from GET ap/documents/note-causes. */
export enum ApNoteCause {
  ShortReceived = 101,
  GoodsReturned = 102,
  FullInvoiceCancellation = 103,
  ReceivedNotInvoiced = 151,
  PriceDifference = 201,
  QualityAllowance = 202,
  EarlyPaymentDiscount = 203,
  SubsequentTradeDiscount = 204,
  FreightOrOtherCharge = 251,
  PriceUnderbilled = 252,
  PriceClauseAdjustment = 253,
  ExemptPurchase = 301,
  TaxOvercharged = 302,
  NonTaxableConcept = 303,
  TaxUndercharged = 351,
  WrongRateApplied = 352,
  VolumeRebate = 401,
  MarketingSupport = 402,
  Bonus = 403,
  SupplierPenalty = 404,
  BalanceReconciliation = 405,
  LatePaymentInterest = 451,
  AdministrativeOrBankCharges = 452,
}

/** Honduras' general ISV rate — what a Financiera line carries when it "brings ISV" (#6324). Mirrors
 *  ApNoteLines.GeneralIsvRate on the API. */
export const GENERAL_ISV_RATE = 0.15;

/**
 * One note line's adjustment (#6324): what the user typed for the note's reason, plus a copy of the original
 * line (the `source*` fields, stamped by the API on every save). Prices and amounts are NET of ISV. The API
 * derives the line's quantity, price, total, ISV and account from it.
 */
export interface ApNoteAdjustment {
  quantity?: number | null;
  priceMode?: ApNotePriceMode | null;
  correctUnitPrice?: number | null;
  amount?: number | null;
  /** "Ajusta ISV" on Precio o valor; "Trae ISV" on Financiera. */
  adjustsTax?: boolean;
  correctTaxRate?: number | null;
  sourceQuantity?: number;
  /** Gross of ISV, as the original line stores it. */
  sourceUnitPrice?: number;
  sourceLineTotal?: number;
  sourceTaxRate?: number;
  sourceTaxAmount?: number;
  sourcePurchaseOrderItemReference?: string | null;
}

export enum Currency {
  USD = 0,
  HNL = 1,
  EUR = 2,
}

/** Where an AP document's exchange rate came from — mirrors Finance.Models ApRateSource. */
export enum ApRateSource {
  DerivedFromAmounts = 1,
  DailyRate = 2,
  /** The Order Invoice / Order Receipt the document was registered from. */
  SourceDocument = 3,
  /** The originating Purchase Order, for an invoice billed against POs. */
  PurchaseOrder = 4,
  /** Typed by the person registering the document, overriding every other source (#5135). */
  Manual = 5,
}

/** The registrable document kinds (excludes the Payment / PaymentRequest tracker discriminators). */
export const REGISTRABLE_AP_DOCUMENT_TYPES: ApDocumentType[] = [
  ApDocumentType.Invoice,
  ApDocumentType.Receipt,
  ApDocumentType.PurchaseReceipt,
  ApDocumentType.Policy,
  ApDocumentType.DebitNote,
  ApDocumentType.CreditNote,
];

/** Amount-only kinds carry a direct amount (no qty/price). Invoice, Receipt, Purchase Receipt and Policy
 * (#6016) are quantity-based (qty × unit price). Debit/Credit Notes are neither since #6324: their lines are
 * derived from the note's reason (see ApNoteAdjustment), so the form gives them their own columns. */
export const AMOUNT_ONLY_AP_DOCUMENT_TYPES: ApDocumentType[] = [];

export interface GlAccountRef {
  reference?: string | null;
  number?: string | null;
}

/** Mirrors Global.Models NumberedDocumentKey ({ reference, number }). */
export interface NumberedDocumentKey {
  reference?: string | null;
  number?: string | null;
}

/** Mirrors Global.Models EntityKey ({ reference, number, entityDefinitionCode }). */
export interface EntityKey {
  reference?: string | null;
  number?: string | null;
  entityDefinitionCode?: number | null;
}

/** Caller-supplied typed reference ({ reference, entityDefinitionCode }) — number resolved server-side. */
export interface EntityKeyByReference {
  reference: string;
  entityDefinitionCode: number;
}

/** GlobalEntityDefinition.OrderInvoice (#4306) — the entity code for a Pre-Invoice (Order Invoice). */
export const ORDER_INVOICE_ENTITY_CODE = 59;

/** GlobalEntityDefinition.OrderReceipt (#4767) — the entity code for a Pre-Invoice sourced from an Order
 * Receipt. Same PreInvoiceReference slot as the Order Invoice; the code is what discriminates them. */
export const ORDER_RECEIPT_ENTITY_CODE = 61;

/** GlobalEntityDefinition.ApDocument (#4471) — the entity code for an AP document (a note's source invoice). */
export const AP_DOCUMENT_ENTITY_CODE = 48;

export interface ApDocumentLine {
  lineNumber: number;
  description?: string | null;
  itemCode?: string | null;
  unitOfMeasure?: number | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  /** PO item tax rate as a fraction (0.15 = 15%). Zero on an exempt or non-PO line. */
  taxRate: number;
  /** Tax already inside `lineTotal` (#4723). API-computed — the UI never sends this. */
  taxAmount: number;
  glAccount?: GlAccountRef | null;
  /** The Purchase Order this line was sourced from (id + number). */
  purchaseOrderKey?: NumberedDocumentKey | null;
  /** The specific PO item this line came from (ItemInPurchaseOrder id). */
  purchaseOrderItemReference?: string | null;
  /** For a Debit/Credit Note (#4471): the source invoice line this line adjusts. */
  sourceLineNumber?: number | null;
  /** For a Debit/Credit Note (#6324): what it adjusts on this line. */
  noteAdjustment?: ApNoteAdjustment | null;
  /** Item classification snapshot (#4842). Copied from the item master and shown read-only when the line has
   * an itemCode; picked by the clerk on a description-only line. Required by the API. */
  itemClassificationCode: string;
}

export interface ApDocument {
  id?: string | null;
  number?: string;
  documentType: ApDocumentType;
  documentDate: string;
  /** When the goods or services were actually received (#5165) — what the due date is measured from when it
   *  is known. Null falls the due date back to `documentDate`. */
  receivedDate?: string | null;
  postingDate: string;
  fiscalYear?: number;
  fiscalPeriod?: number;
  dueDate?: string;
  creditDays: number;
  /** Payment term the due date was derived from (#4784). API-resolved; read-only on the form. */
  paymentTermCode?: string | null;
  /** Where `paymentTermCode` came from (#4784). */
  paymentTermSource?: PaymentTermSource;
  /** The provider master's default term at registration (#4784) — compared against `paymentTermCode`
   *  to flag a source document negotiated on different terms. */
  providerPaymentTermCode?: string | null;
  providerReference?: string | null;
  providerCode?: string;
  providerName?: string;
  /** Legacy only (#4457): one-time vendors are no longer accepted — every AP document has a Business. */
  oneTimeVendorName?: string | null;
  /** #4785 — AP control account name, snapshotted beside the number. Localized and API-owned. */
  apAccountName?: ResponseTranslation | null;
  financialCompanyReference: string;
  /** Distinct POs this document draws lines from (derived from the lines, server-side). */
  purchaseOrderKeys?: NumberedDocumentKey[];
  /** Draws on an Administrative purchase order (#5942): listed for everyone, opened only by role holders. */
  hasAdministrativePurchaseOrder?: boolean;
  fiscalNumber?: string | null;
  /** The provider's own internal reference — free text, never validated or joined on (#5029). */
  providerReferenceNumber?: string | null;
  cai?: string | null;
  providerTaxId?: string | null;
  subTotal: number;
  taxAmount: number;
  totalAmount: number;
  paidAmount?: number;
  /** Withholding settled against this invoice by posted Constancia de Retención documents (#4454). Reduces
   * balanceDue alongside paidAmount, so the invoice is payable only for its net. */
  withheldAmount?: number;
  advanceAppliedAmount?: number;
  /** Credit applied when a Credit Note posted (#6324): on an invoice, the credit it received; on a credit
   *  note, the part spent on its original. An invoice's balanceDue is already net of it, and a credit note's
   *  balanceDue is the provider's open credit ("saldo a favor"). */
  creditAppliedAmount?: number;
  balanceDue?: number;
  currency: Currency;
  /** Currency → functional multiplier; what the GL posted with. Not the rate a user reads — see rateToUsd. */
  exchangeRate: number;
  functionalCurrency: Currency;
  functionalAmount?: number;
  /** The rate the document was booked at, quoted on the USD pivot: units of `currency` per 1 USD (27 for
   * HNL, 0.89 for EUR). Stamped once at registration, so reopening the document shows the rate it was
   * actually booked at rather than today's. Null on documents registered before this was captured. */
  rateToUsd?: number | null;
  rateSource?: ApRateSource;
  apAccountReference?: string | null;
  apAccountNumber?: string | null;
  /** The provider master's AP control account AS IT STANDS NOW — hydrated on read, never stored, and never
   *  sent back. The document's own `apAccountReference` is pinned at registration and keeps posting the
   *  credit side whatever happens to the provider afterwards; this pair exists only so the form can tell the
   *  clerk the two have drifted apart. Both null once the provider's account has been cleared. */
  providerApAccountReference?: string | null;
  providerApAccountNumber?: string | null;
  journalEntryReference?: string | null;
  journalEntryDocumentNumber?: string | null;
  /** Whether this document needs a posted Constancia de Retención before it can be paid (#4842). Derived
   * API-side from the line classifications (true when any line is a service) and defaulting to true. The
   * clerk may waive it while the document is a draft — see the waiver fields below. */
  requiresWithholding: boolean;
  /** Why the clerk waived the requirement (#4842) — always present when requiresWithholding is false. */
  withholdingWaivedReason?: string | null;
  withholdingWaivedBy?: string | null;
  withholdingWaivedDate?: string | null;

  /** The clerk asserted a certificate is needed regardless of the line classifications (#5166). Also makes
   *  every line withholdable — goods included — so the certificate it now demands can actually be built. */
  withholdingForced?: boolean;
  /** Why it was forced. Always present when `withholdingForced` is true. */
  withholdingForcedReason?: string | null;
  withholdingForcedBy?: string | null;
  withholdingForcedDate?: string | null;
  status: ApDocumentStatus;
  notes?: string | null;
  /** When registered from an Order "Pre-Invoice" (#4306): the source Order Invoice (id + number + code). */
  preInvoiceReference?: EntityKey | null;
  /** Debit/Credit Note (#4471) or Retention (#4454): the original invoice this document adjusts / withholds
   * against (id + number + entity code). Null on a Financiera note (#6324). */
  relatedDocumentReference?: EntityKey | null;
  /** Why a Credit/Debit Note was issued (#6324). Null on every other kind. */
  noteReason?: ApNoteReason | null;
  /** Optional cause within the reason, for reports only (#6324). */
  noteCause?: ApNoteCause | null;
  lineItems: ApDocumentLine[];
  /**
   * Supporting files — the provider's scanned invoice, a delivery note, an email authorising a charge.
   *
   * `name` is the durable S3 key and the id used to remove one; `presignedUrl` expires, so it is re-issued
   * on every read and is not worth holding on to between loads. Managed through the two attachment
   * endpoints, never by sending this list back on an update.
   */
  attachments?: S3Object[];
  createdBy?: string;
  createdDate?: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

// Create-draft payload — mirrors ApDocumentInsert (Number/Status/amounts derived server-side).
export interface ApDocumentLineInsert {
  lineNumber: number;
  description?: string | null;
  itemCode?: string | null;
  unitOfMeasure?: number | null;
  quantity: number;
  unitPrice: number;
  /** Sent so the API can extract the embedded tax; the amount itself is API-owned. */
  taxRate: number;
  glAccount?: GlAccountRef | null;
  purchaseOrderKey?: NumberedDocumentKey | null;
  purchaseOrderItemReference?: string | null;
  /** Debit/Credit Note (#4471): the original invoice line this line adjusts. */
  sourceLineNumber?: number | null;
  /** Debit/Credit Note (#6324): what it adjusts on this line. Required on every note line. */
  noteAdjustment?: ApNoteAdjustment | null;
  /** Item classification (#4842) — REQUIRED on every line: the API's ApDocumentLine marks it `required`, so a
   * payload missing it fails JSON binding outright. The API re-asserts it from the item master whenever the
   * line has an itemCode, so what matters here is that a description-only line carries the clerk's choice. */
  itemClassificationCode: string;
}

export interface ApDocumentInsert {
  /** A rate the user typed, quoted units-per-USD (#5135). Null — the normal case — leaves the API to book
   *  from the source document, the purchase order or the day's rate. Bounded server-side by
   *  ExchangeRateBoundsPolicy, and mirrored in `utilities/exchange-rate-bounds` so the field can refuse a
   *  slipped decimal as it is typed. */
  rateToUsd?: number | null;

  documentType: ApDocumentType;
  providerReference?: string | null;
  oneTimeVendorName?: string | null;
  financialCompanyReference: string;
  documentDate: string;
  /** #5165 — required for an Invoice, optional elsewhere; never earlier than `documentDate`. */
  receivedDate?: string | null;
  postingDate: string;
  creditDays: number;
  currency: Currency;
  fiscalNumber?: string | null;
  /** The provider's own internal reference — free text, never validated or joined on (#5029). */
  providerReferenceNumber?: string | null;
  cai?: string | null;
  providerTaxId?: string | null;
  functionalAmount?: number | null;
  apAccountReference?: string | null;
  apAccountNumber?: string | null;
  /** #4784/#4785 — an override attempt. The API discards it whenever a source PO / Receipt dictates the
   *  term, so it can only take effect on a provider-default document. */
  paymentTermCode?: string | null;
  notes?: string | null;
  /** Register from an Order "Pre-Invoice" (#4306): the server derives provider + lines from the invoice's
   * POs and consumes it. When set, providerReference/lineItems may be omitted. */
  preInvoiceReference?: EntityKey | null;
  /** Debit/Credit Note (#4471): the original invoice this note adjusts (reference + number +
   * entityDefinitionCode). The server validates same provider + currency and (for credit notes) the
   * per-line cumulative cap, and re-stamps the number from the resolved original. Omitted on a Financiera note. */
  relatedDocumentReference?: EntityKey | null;
  /** Required on a Credit/Debit Note (#6324). */
  noteReason?: ApNoteReason | null;
  noteCause?: ApNoteCause | null;
  lineItems: ApDocumentLineInsert[];
}

export interface ApDocumentSearchCommand {
  financialCompanyReference?: string | null;
  providerReference?: string | null;
  number?: string | null;
  fiscalNumber?: string | null;
  documentType?: ApDocumentType | null;
  /** When set, matches ANY of these types (takes precedence over `documentType`). Used by the
   * withholding screen to search Invoices and Receipts together (#4457). */
  documentTypes?: ApDocumentType[] | null;
  status?: ApDocumentStatus | null;
  currency?: Currency | null;
  documentDateFrom?: string | null;
  documentDateTo?: string | null;
  dueDateFrom?: string | null;
  dueDateTo?: string | null;
  amountFrom?: number | null;
  amountTo?: number | null;
  overdueOnly?: boolean | null;
  /** Narrows by withholding requirement (#4842). The Constancia screen sets true so its invoice picker only
   * offers documents a certificate can actually be issued against. Server-side: filtering a paged result
   * client-side would silently thin the page and leave the picker looking empty. */
  requiresWithholding?: boolean | null;
  /** Credit/Debit Notes of one reason only (#6324). */
  noteReason?: ApNoteReason | null;
}

/** Preview of the next self-issued Purchase Receipt fiscal number (#4465) — read-only, no reservation:
 * the correlativo is only drawn when the PurchaseReceipt POSTS, so this is "expected", not promised. */
export interface PurchaseReceiptNextNumber {
  nextNumber: string;
  cai: string;
  endDate: string;
  remainingInRange: number;
}

export interface ApDocumentSummary {
  functionalCurrency: Currency;
  openPayable: number;
  postedCount: number;
  dueThisWeek: number;
  dueThisWeekCount: number;
  draftCount: number;
  overdueCount: number;
}

/** A payable base document bundled with its Credit/Debit Notes + the withholding already applied by posted
 * Retenciones + the computed net still payable (#4514). Feeds the payment pickers so the UI can show the true
 * net (and why it's adjusted) without fanning out N queries. Mirrors Finance.Models PayableDocumentDto. */
export interface PayableDocument {
  document: ApDocument;
  creditNotes: ApDocument[];
  debitNotes: ApDocument[];
  /** Withholding already applied to the base by posted Retenciones (= document.withheldAmount). */
  withheldAmount: number;
  /** document.balanceDue + Σ debitNotes.balanceDue. The base's balance is already net of its credit notes,
   *  which were applied when they posted (#6324); `creditNotes` is listed for context only. */
  netPayable: number;
  /** True when a Credit/Debit Note or withholding moved the net away from the base's own balanceDue. */
  hasAdjustments: boolean;
  /** Whether a Constancia de Retención has already been POSTED against this document (#4488/#4842).
   * NOT inferable from withheldAmount: a zero "No aplica" certificate posts at 0, so 0 means both "no
   * certificate" and "certificate posted, nothing withheld". */
  hasPostedWithholding: boolean;
  /** Whether the document may be selected for payment — it needs no certificate (goods-only or waived) or
   * already has a posted one. False ⇒ the row must be disabled: the payment, payment request and cash
   * request all reject it server-side. Derived API-side so the picker and the services cannot disagree. */
  withholdingSatisfied: boolean;
  /** The posted Constancias that produced `withheldAmount` (#4454/#5122) — which certificate reduced this
   *  document, not just by how much. Empty when nothing has been withheld. Posted only: a draft has moved
   *  no balance. */
  withholdings: PayableWithholding[];
}

/**
 * One posted Constancia de Retención as it appears against a payable document (#4454/#5122).
 *
 * A certificate can cite several AP documents, so the identity here is the whole certificate's while
 * `withheldAmount` is only this document's share of it.
 */
export interface PayableWithholding {
  reference?: string | null;
  /** Our internal sequence (WHD-2026-000012). */
  number: string;
  /** The fiscal correlativo printed on the certificate the provider receives, where one was drawn. */
  certificateNumber?: string | null;
  /** When it posted — which is when this document's balance moved. */
  issuedDate?: string | null;
  withheldAmount: number;
  /** The remittance that paid this withholding to SAR (#4489), once one has. Null while issued but not yet
   *  filed, which is the ordinary state for most of a month and is not a problem: the payable was reduced
   *  when the certificate posted, long before SAR is paid. */
  remittanceReference?: string | null;
  remittanceNumber?: string | null;
  remittedDate?: string | null;
}

/** A single per-document allocation produced by distributing a payable group's amount (#4516). */
export interface PayableAllocationEntry {
  apDocumentReference: string;
  amount: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** A payable row that is a base document (Invoice/Receipt/PurchaseReceipt/Policy) carrying its notes — as
 * opposed to a standalone Credit/Debit Note that is its own row (its base is no longer payable). Only a base
 * group is distributed doc-first; a standalone note allocates directly. */
export function isPayableBaseGroup(p: PayableDocument): boolean {
  return !NOTE_AP_DOCUMENT_TYPES.includes(p.document.documentType);
}

/** All document ids that make up a payable group (base + its debit notes) — used to fold a saved
 * per-document allocation back into one group amount when reopening a request/payment. Credit notes are not
 * members (#6324): open credit is its own row under "Créditos del proveedor". */
export function payableGroupRefs(p: PayableDocument): string[] {
  return [p.document.id ?? '', ...p.debitNotes.map((d) => d.id ?? '')].filter((id): id is string => !!id);
}

/**
 * Distribute a single "amount to pay" across a payable group DOC-FIRST (#4516): the base document absorbs the
 * amount first, then each debit note in turn. The returned per-document entries never exceed any document's
 * balance and add up to `amount` — so the submission payload stays one row per document, only the input is
 * grouped.
 *
 * Credit notes take no part (#6324): they were applied to the base when they posted, so its balance is already
 * net of them, and any credit left over is its own row under "Créditos del proveedor".
 */
export function distributePayable(p: PayableDocument, amount: number): PayableAllocationEntry[] {
  const entries: PayableAllocationEntry[] = [];
  let pool = round2(amount);

  const base = p.document;
  const baseBalance = base.balanceDue ?? base.totalAmount ?? 0;
  const baseAmount = round2(Math.min(Math.max(pool, 0), baseBalance));
  if (baseAmount > 0 && base.id) entries.push({ apDocumentReference: base.id, amount: baseAmount });
  pool = round2(pool - baseAmount);

  for (const dn of p.debitNotes) {
    const bal = dn.balanceDue ?? dn.totalAmount ?? 0;
    const amt = round2(Math.min(Math.max(pool, 0), bal));
    if (amt > 0 && dn.id) entries.push({ apDocumentReference: dn.id, amount: amt });
    pool = round2(pool - amt);
  }

  return entries;
}

/** The signed default amount for a payable row: a base group defaults to its net payable; a standalone credit
 * note is negative (it reduces the payment); a standalone debit note is its positive balance. */
export function payableRowDefault(p: PayableDocument): number {
  if (isPayableBaseGroup(p)) return p.netPayable;
  const balance = p.document.balanceDue ?? p.document.totalAmount ?? 0;
  return p.document.documentType === ApDocumentType.CreditNote ? -round2(balance) : round2(balance);
}
